import React, { useState } from 'react';
import { Navigate } from "react-router-dom";
import {
  Edit,
  ArrowBack,
  DeleteForever
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase,
  FeatureSource,
  FeatureBase,
  PlayerObject,
} from "@models";
import { 
  OrgInstance,
  Campaign,
  Encounter
} from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";

import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props { 
  obj: ModelBase | Campaign | Encounter | OrgInstance;
  replace_edit?: () => React.ReactNode;
}

const api = API.getInstance();

export default function ObjectDetails({ 
  obj, 
  replace_edit,
}: Props) {
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  const [level_filter, setLevelFilter] = useState(-1);

  const renderFeatureBases = () => {
    if (obj instanceof FeatureSource) {
      const height = window.innerHeight - 64;
      return (
        <Grid size={12}>
          <FeatureBasesInput 
            feature_bases={obj.feature_bases} 
            feature_source={obj}
            onExpand={setExpandedFeatureBase}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            height={height}
            onAdd={() => { }}
            onReorder={() => { }}
          />
        </Grid>
      );
    }
    return null;
  }
  
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (expanded_feature_base && obj instanceof FeatureSource) {
    return (
      <FeatureBaseInput
        parent_name={obj.name}
        feature_base={expanded_feature_base} 
        feature_bases={obj.feature_bases}
        feature_source={obj}
        onChange={(changed: FeatureBase) => { }}
        onDelete={() => { }}
        onDone={() => { setExpandedFeatureBase(null); }}
      />
    );
  } else {
    const data_type = obj.data_type;
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
    const plural = dt_info?.plural??"";
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <Tooltip title={`Back to ${plural}`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                setNavigateTo(`/${data_type}`);
              }}>
              <ArrowBack/>
            </Fab>
          </Tooltip> 
          &nbsp;
          <Tooltip title={`Delete ${obj.name}`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              disabled={!api.online}
              onClick={ () => {
                api.deleteObject(data_type, obj).then(res => {
                  setNavigateTo(`/${data_type}`);
                });
              }}>
              <DeleteForever/>
            </Fab>
          </Tooltip> 
        </Grid>
        <Grid size={12}>
          <span className="header">
            { obj.name }
          </span>
          { replace_edit ? 
            replace_edit() 
          : api.user && (obj.owner_id === api.user._id || api.user.admin) &&
            <Tooltip title={`Edit ${obj.name}`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                disabled={!api.online}
                onClick={ () => {
                  setNavigateTo(`/${data_type}/${obj._id}`);
                }}>
                <Edit/>
              </Fab>
            </Tooltip> 
          }
        </Grid>
        { !(obj instanceof PlayerObject) &&
          <Grid
            size={{
              xs: 12
            }}>
            <Grid container spacing={0} direction="column">
              <Grid className="header">
                Description
              </Grid>
              <Grid>
                <DisplayStringWithReplacements display_string={ obj.description } />
              </Grid>
            </Grid>
          </Grid>
        }
        { renderFeatureBases() }
      </Grid>
    ); 
  } 
}
